import { pick } from '@jamesgmarks/utilities';
import { UnionOfArrayElements } from '@/shared/types/union-of-array-elements';
import { RsModule } from './types/rs-module';
import { TypedModule } from './types/typed-module';
import { TRemappedGettersModuleReturn, TMappedGettersModuleReturn } from './types/mapped-types';

/** Used internally to this module. Maps using the dictionary strategy. */
const mapGettersForModuleFromMap = <
  TState extends object,
  TModule extends RsModule<TState>,
  TNewKeys extends string[],
  TKeys extends(keyof TypedModule<TState, TModule>['getters'])[],
  TMap extends Record<UnionOfArrayElements<TNewKeys>, UnionOfArrayElements<TKeys>>,
>(
  module_: TypedModule<TState, TModule>,
  getterMap: TMap,
) => {
  const _getterKeys = Object.keys(getterMap) as unknown as TNewKeys;

  const result = _getterKeys.reduce((acc, newKey: keyof typeof getterMap) => {
    const oldKey = getterMap[newKey];
    return {
      ...acc,
      [newKey]: module_.getters[oldKey],
    };
  }, {} as Record<string, any>);

  return result as TRemappedGettersModuleReturn<TState, TModule, TNewKeys, TKeys, TMap>;
};

/** Used internally to this module. Maps using the string array strategy. */
const mapGettersForModuleByArray = <
  TState extends object,
  TModule extends RsModule<TState>,
  TKeys extends(keyof TypedModule<TState, TModule>['getters'])[]
>(
  module_: TypedModule<TState, TModule>,
  getterKeys: TKeys,
) => {
  const result = pick(module_.getters, getterKeys);

  return result as TMappedGettersModuleReturn<TState, TModule, TKeys>;
};

/**
 * Extracts the getters from a typed module and maps them to computed properties in a Vue component definition.
 *
 * @param module_ The typed module to map getters from.
 * @param actionKeys Which getters to map. Use a string array or remap using a dictionary where the keys
 *                   are the name you want to use and the values are the names originally used in the module.
 */
export function mapModuleGetters <
  TState extends object,
  TModule extends RsModule<TState>,
  TNewKeys extends string[],
  TKeys extends(keyof TypedModule<TState, TModule>['getters'])[],
  TMap extends Record<UnionOfArrayElements<TNewKeys>, UnionOfArrayElements<TKeys>>,
>(
  module_: TypedModule<TState, TModule>,
  getterMap: TMap): TRemappedGettersModuleReturn<TState, TModule, TNewKeys, TKeys, TMap>;

export function mapModuleGetters <
  TState extends object,
  TModule extends RsModule<TState>,
  TKeys extends(keyof TypedModule<TState, TModule>['getters'])[]
>(
  module_: TypedModule<TState, TModule>,
  getterKeys: TKeys): TMappedGettersModuleReturn<TState, TModule, TKeys>;

export function mapModuleGetters <
  TState extends object,
  TModule extends RsModule<TState>,
  TNewKeys extends string[],
  TKeys extends(
    keyof TypedModule<TState, TModule>['getters'])[],
  TMap extends Record<UnionOfArrayElements<TNewKeys>, UnionOfArrayElements<TKeys>>,
>(
  module_: TypedModule<TState, TModule>,
  getterKeys: TMap | TKeys,
) {
  if (Array.isArray(getterKeys)) {
    return mapGettersForModuleByArray(module_, getterKeys);
  }
  return mapGettersForModuleFromMap(module_, getterKeys);
}
